import { Box, CircularProgress, Divider, Grid, Typography, makeStyles } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { Button, StandardDialog } from '@roc/ui';
import { HistoryEntry } from '../types/types';
import { usePayablesDashboardStore } from '../hooks/usePayablesDashboardStore';

const useStyles = makeStyles(theme => ({
  scrollContent: {
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: theme.spacing(1),
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  entryBox: {
    padding: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: '0 4px 4px 0',
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  changeHeader: {
    padding: `${theme.spacing(0.5)}px 0`,
    marginBottom: theme.spacing(0.5),
  },
  changeRow: {
    padding: `${theme.spacing(0.75)}px 0`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  fieldLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  oldValue: {
    color: theme.palette.error.main,
    textDecoration: 'line-through',
    wordBreak: 'break-word' as const,
  },
  arrow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  newValue: {
    color: theme.palette.success.main,
    wordBreak: 'break-word' as const,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
}));

interface HistoryModalProps {
  open: boolean;
  history: HistoryEntry[];
  isLoading: boolean;
  onClose: () => void;
  adjustmentOnly?: boolean;
}

export const HistoryModal = ({ open, history, isLoading, onClose, adjustmentOnly = false }: HistoryModalProps) => {
  const classes = useStyles();
  const { payablesApprovedStore } = usePayablesDashboardStore();


  const filteredHistory = adjustmentOnly
    ? history.filter(entry => entry.changes.some(c => c.fieldName?.toLowerCase().includes('adjustment')))
    : history;

  const getContent = () => {
    if (isLoading) {
      return (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      );
    }

    if (!filteredHistory || filteredHistory.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body1">No history records found.</Typography>
        </Box>
      );
    }

    return (
      <Box className={classes.scrollContent}>
        {filteredHistory.map((entry, index) => (
          <Box key={index} className={classes.entryBox}>
            <Box className={classes.entryHeader}>
              <Typography variant="subtitle2" color="primary">
                {entry.updateDate}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {entry.updatedBy}
              </Typography>
            </Box>
            {entry.changes.length > 0 && (
              <>
                <Divider style={{ marginBottom: 8 }} />
                <Grid container className={classes.changeHeader}>
                  <Grid item xs={4}>
                    <Typography variant="caption" className={classes.fieldLabel}>Field</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" className={classes.fieldLabel}>Previous value</Typography>
                  </Grid>
                  <Grid item xs={1} />
                  <Grid item xs={3}>
                    <Typography variant="caption" className={classes.fieldLabel}>New value</Typography>
                  </Grid>
                </Grid>
              </>
            )}
            {entry.changes.map((change, changeIndex) => (
              <Grid container key={changeIndex} alignItems="center" className={classes.changeRow}>
                <Grid item xs={4}>
                  <Typography variant="body2">
                    {change.fieldName}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" className={classes.oldValue}>
                    {change.oldValue ?? '—'}
                  </Typography>
                </Grid>
                <Grid item xs={1} className={classes.arrow}>
                  <ArrowForward fontSize="small" />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" className={classes.newValue}>
                    {change.newValue ?? '—'}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <StandardDialog
      open={open}
      title={`Invoice #${payablesApprovedStore.historyRecord?.invoice} - ${adjustmentOnly ? 'Adjusment ' : ''}History`}
      maxWidth="md"
      fullWidth
      handleClose={onClose}
      dialogContent={getContent}
      dialogActions={
        <Button onClick={onClose} color="primary" variant="outlined" testId="close-history">
          Close
        </Button>
      }
    />
  );
};
